import { BaseAnalyticsProvider } from '../../BaseAnalyticsProvider'
import { MixpanelEventMethod } from './Mixpanel.types'
import type { ActionDataKey, ProviderConfiguration, EventConfiguration } from '../../AnalyticsManager.types'
import { AnalyticsProvider, AnalyticsDevice, ActionName } from '../../AnalyticsManager.types'
import { applyTransformations } from '../../actionDataTransformations'
import MixpanelWebSDK from './MixpanelWebSDK'

import type { MixpanelEventData, MixpanelDependency, MixpanelSDKConfiguration } from './Mixpanel.types'

export default class Mixpanel extends BaseAnalyticsProvider {
	private configuration: ProviderConfiguration

	private dependencies: MixpanelDependency

	private webSdk: MixpanelWebSDK | null = null

	private webSdkEnabled: boolean

	private mobileSdkEnabled: boolean

	constructor(configuration: ProviderConfiguration, dependencies: MixpanelDependency) {
		super()

		this.dependencies = dependencies
		this.configuration = configuration

		this.mobileSdkEnabled = !!configuration.origin?.includes(AnalyticsDevice.MOBILE_APPLICATION) && this.dependencies.isMobileApp()
		this.webSdkEnabled = !!configuration.origin?.includes(AnalyticsDevice.WEB_BROWSER) && !this.dependencies.isMobileApp()

		if(this.webSdkEnabled) {
			this.webSdk = new MixpanelWebSDK(configuration.other as MixpanelSDKConfiguration)
		}
	}

	private static buildEventData(
		eventName: string,
		actionData: Record<ActionDataKey, unknown>,
		eventConfiguration: EventConfiguration,
		actionName: ActionName
	): MixpanelEventData {
		const properties: Record<string, unknown> = {}
		let method = MixpanelEventMethod.TRACK

		eventConfiguration.forEach((eventAttributeData) => {
			const eventProperty = eventAttributeData.attributeName
			const transformations = eventAttributeData?.transformations
			const actionDataKey = eventAttributeData?.dataKey

			if (!actionDataKey || !eventProperty) {
				return
			}

			properties[eventProperty] = applyTransformations(
				transformations,
				Object.prototype.hasOwnProperty.call(actionData, actionDataKey)
					? (actionData[actionDataKey as ActionDataKey] as string)
					: actionDataKey
			)
		})

		if (actionName === ActionName.SET_SUPER_PROPERTIES && eventName === '__setSuperProperties__') {
			method = MixpanelEventMethod.SET_SUPER_PROPERTIES
		} else if (actionName === ActionName.SET_USER_PROPERTIES && eventName === '__setUserProperties__') {
			method = MixpanelEventMethod.SET_USER_PROPERTIES
		}

		return {
			eventName,
			arguments: [properties],
			method,
		}
	}

	public async sendEvent(actionName: ActionName, actionData: Record<ActionDataKey, any>): Promise<void> {
		const actionConfiguration = this.configuration?.actions[actionName]
		if (!actionConfiguration || (!this.webSdkEnabled && !this.mobileSdkEnabled)) {
			return
		}

		Object.keys(actionConfiguration).forEach((eventName) => {
			const eventData = Mixpanel.buildEventData(eventName, actionData, actionConfiguration[eventName], actionName)

			if (this.mobileSdkEnabled) {
				this.dependencies.sendEvent(AnalyticsProvider.MIXPANEL, eventData)
			} else if (this.webSdkEnabled) {
				this.webSdk?.sendEvent(eventData)
			}
		})
	}

	public async identifyUser(userId: string) {
		if (this.webSdkEnabled) {
			this.webSdk?.identifyUser(userId)
		} else if (this.mobileSdkEnabled) {
			this.dependencies.sendEvent(AnalyticsProvider.MIXPANEL, {
				eventName: '',
				method: MixpanelEventMethod.IDENTIFY_USER,
				arguments: [userId],
			})
		}
	}

	public async resetUser() {
		if (this.webSdkEnabled) {
			this.webSdk?.resetUser()
		} else if (this.mobileSdkEnabled) {
			this.dependencies.sendEvent(AnalyticsProvider.MIXPANEL, {
				eventName: '',
				method: MixpanelEventMethod.RESET_USER,
				arguments: null
			})
		}
	}
}
